"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppStoreOptional } from "../contexts/AppStoreContext";
import { useDesktopContext } from "../contexts/DesktopContext";
import type { DesktopIconConfig } from "../types";
import { isWindowMinimized, isWindowOpen } from "../types";
import {
  GRID_COLS,
  GRID_GAP_COL,
  GRID_GAP_ROW,
  GRID_PADDING_X_RATIO,
  GRID_PADDING_Y,
  GRID_ROWS,
  ICON_FONT_RATIO,
  ICON_MAX_PX,
  ICON_MIN_PX,
  ICON_RADIUS_RATIO,
  ICON_SIZE_RATIO,
  LABEL_FONT_MAX_PX,
  LABEL_FONT_MIN_PX,
  LABEL_FONT_RATIO,
  LABEL_GAP_RATIO,
} from "./takeoffConfig";

/* ==========================================================================
   TYPES
   ========================================================================== */

export interface TakeoffProps {
  isOpen: boolean;
  onClose: () => void;
  /** All launchable icons (including hidden ones) */
  icons: DesktopIconConfig[];
  onIconClick: (iconConfig: DesktopIconConfig) => void;
  onOpenSpotlight: () => void;
  /** Whether an icon is currently in the dock */
  isIconInDock?: (iconId: string) => boolean;
  /** Whether an icon is currently on the desktop */
  isIconOnDesktop?: (iconId: string) => boolean;
  /** Whether the dock is full */
  isDockFull?: boolean;
  /** Called when user selects "Move to Dock" */
  onMoveToDock?: (iconId: string) => void;
  /** Called when user selects "Move to Desktop" */
  onMoveToDesktop?: (iconId: string) => void;
  /** Called when user selects "Hide" (move to Takeoff only) */
  onHideIcon?: (iconId: string) => void;
}

/* ==========================================================================
   LAYOUT MATH
   ========================================================================== */

/** Pixel size that DesktopIcon renders custom (non-emoji) icons at — i.e. h-14/w-14 in DesktopIcon.tsx. Scaled up via CSS transform in Takeoff. */
const NATIVE_ICON_PX = 56;

interface GridMetrics {
  cellWidth: number;
  cellHeight: number;
  iconSize: number;
  iconFontSize: number;
  iconRadius: number;
  labelFontSize: number;
  labelGap: number;
}

function computeMetrics(width: number, height: number): GridMetrics {
  const usableW = Math.max(
    0,
    width - width * GRID_PADDING_X_RATIO * 2 - GRID_GAP_COL * (GRID_COLS - 1)
  );
  const usableH = Math.max(
    0,
    height - GRID_PADDING_Y * 2 - GRID_GAP_ROW * (GRID_ROWS - 1)
  );
  const cellWidth = usableW / GRID_COLS;
  const cellHeight = usableH / GRID_ROWS;
  // Reserve space inside each cell for label + label gap, so the icon never
  // overflows into the next row.
  const labelReserve = LABEL_FONT_MAX_PX + 6;
  const cellAvail = Math.max(0, Math.min(cellWidth, cellHeight - labelReserve));
  const iconSize = Math.max(
    ICON_MIN_PX,
    Math.min(ICON_MAX_PX, cellAvail * ICON_SIZE_RATIO)
  );
  const iconFontSize = iconSize * ICON_FONT_RATIO;
  const iconRadius = iconSize * ICON_RADIUS_RATIO;
  const labelFontSize = Math.max(
    LABEL_FONT_MIN_PX,
    Math.min(LABEL_FONT_MAX_PX, iconSize * LABEL_FONT_RATIO)
  );
  const labelGap = iconSize * LABEL_GAP_RATIO;
  return {
    cellWidth,
    cellHeight,
    iconSize,
    iconFontSize,
    iconRadius,
    labelFontSize,
    labelGap,
  };
}

/* ==========================================================================
   COMPONENT
   ========================================================================== */

export function Takeoff({
  isOpen,
  onClose,
  icons,
  onIconClick,
  onOpenSpotlight,
  isIconInDock,
  isIconOnDesktop,
  isDockFull = false,
  onMoveToDock,
  onMoveToDesktop,
  onHideIcon,
}: TakeoffProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const iconsPerPage = GRID_COLS * GRID_ROWS;
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const metrics = useMemo(
    () => computeMetrics(containerSize.width, containerSize.height),
    [containerSize.width, containerSize.height]
  );
  const touchStartRef = useRef({ x: 0 });
  const touchMoveRef = useRef({ x: 0 });
  const dragRef = useRef({
    active: false,
    startX: 0,
    currentX: 0,
    startPage: 0,
  });
  const [dragOffset, setDragOffset] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const appStore = useAppStoreOptional();
  /** When true, the next click event should NOT close Takeoff (drag just ended). */
  const suppressCloseRef = useRef(false);
  const pagesContainerRef = useRef<HTMLDivElement>(null);
  const { windows } = useDesktopContext();

  // Observe pages-container size so the grid scales with viewport / window resize
  useEffect(() => {
    if (!isOpen) return;
    const el = pagesContainerRef.current;
    if (!el) return;
    const update = () => {
      setContainerSize({
        width: el.clientWidth,
        height: el.clientHeight,
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    iconId: string;
    x: number;
    y: number;
  } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Map windowId -> category from AppStore catalog
  const categoryByWindowId = useMemo(() => {
    const map = new Map<string, string>();
    if (appStore) {
      for (const entry of appStore.catalog) {
        map.set(entry.appId, entry.category);
      }
    }
    return map;
  }, [appStore]);

  const OTHER_CATEGORY = "Other";

  // Unique categories present among the current icons (sorted). Icons without
  // a catalog category are bucketed into "Other" (appended last).
  const categories = useMemo(() => {
    const set = new Set<string>();
    let hasUncategorized = false;
    for (const icon of icons) {
      const cat = categoryByWindowId.get(icon.windowId);
      if (cat) set.add(cat);
      else hasUncategorized = true;
    }
    const sorted = Array.from(set).sort();
    if (hasUncategorized) sorted.push(OTHER_CATEGORY);
    return sorted;
  }, [icons, categoryByWindowId]);

  // Sort icons alphabetically, filter by category if selected, then split into pages
  const sortedIcons = useMemo(() => {
    const base = [...icons].sort((a, b) => a.label.localeCompare(b.label));
    if (!filterOpen || !selectedCategory) return base;
    if (selectedCategory === OTHER_CATEGORY) {
      return base.filter((i) => !categoryByWindowId.has(i.windowId));
    }
    return base.filter(
      (i) => categoryByWindowId.get(i.windowId) === selectedCategory
    );
  }, [icons, filterOpen, selectedCategory, categoryByWindowId]);

  // Reset filter when Takeoff closes
  useEffect(() => {
    if (!isOpen) {
      setFilterOpen(false);
      setSelectedCategory(null);
    }
  }, [isOpen]);

  const pages = useMemo(() => {
    const result: DesktopIconConfig[][] = [];
    for (let i = 0; i < sortedIcons.length; i += iconsPerPage) {
      result.push(sortedIcons.slice(i, i + iconsPerPage));
    }
    if (result.length === 0) result.push([]);
    return result;
  }, [sortedIcons, iconsPerPage]);

  const totalPages = pages.length;

  // Clamp current page if icons change
  useEffect(() => {
    if (currentPage >= totalPages) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [currentPage, totalPages]);

  // Reset page on open
  useEffect(() => {
    if (isOpen) setCurrentPage(0);
  }, [isOpen]);

  // Reset to first page when filter selection changes
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedCategory, filterOpen]);

  // Close context menu on click outside or Escape
  useEffect(() => {
    if (!contextMenu) return;

    const handleClick = (e: MouseEvent) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(e.target as Node)
      ) {
        setContextMenu(null);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setContextMenu(null);
    };

    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClick);
      document.addEventListener("keydown", handleEscape);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [contextMenu]);

  // Close on Escape (Takeoff itself)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !contextMenu) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose, contextMenu]);

  /** Only close if we didn't just finish a drag. */
  const safeClose = useCallback(() => {
    if (suppressCloseRef.current) return;
    onClose();
  }, [onClose]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current.x = e.touches[0].clientX;
    touchMoveRef.current.x = e.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchMoveRef.current.x = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const deltaX = touchStartRef.current.x - touchMoveRef.current.x;
    if (Math.abs(deltaX) > 50) {
      if (deltaX > 0 && currentPage < totalPages - 1) {
        setCurrentPage((p) => p + 1);
      } else if (deltaX < 0 && currentPage > 0) {
        setCurrentPage((p) => p - 1);
      }
    }
  }, [currentPage, totalPages]);

  // Mouse drag to swipe between pages (and suppress close on any drag)
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0 || totalPages <= 1) return;
      suppressCloseRef.current = false;
      dragRef.current = {
        active: true,
        startX: e.clientX,
        currentX: e.clientX,
        startPage: currentPage,
      };
      setDragOffset(0);
    },
    [currentPage, totalPages]
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current.active) return;
      dragRef.current.currentX = e.clientX;
      const containerWidth =
        pagesContainerRef.current?.offsetWidth ?? window.innerWidth;
      const delta = e.clientX - dragRef.current.startX;
      setDragOffset((delta / containerWidth) * 100);
    };

    const handleMouseUp = () => {
      if (!dragRef.current.active) return;
      const deltaX = dragRef.current.startX - dragRef.current.currentX;
      const moved = Math.abs(deltaX) > 5;

      // Any meaningful mouse movement → suppress the upcoming click so we don't close Takeoff
      if (moved) {
        suppressCloseRef.current = true;
        // Reset after the click event has (or hasn't) fired
        requestAnimationFrame(() => {
          setTimeout(() => {
            suppressCloseRef.current = false;
          }, 0);
        });
      }

      const containerWidth =
        pagesContainerRef.current?.offsetWidth ?? window.innerWidth;
      const threshold = containerWidth * 0.15; // 15% of width to trigger page change

      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0 && dragRef.current.startPage < totalPages - 1) {
          setCurrentPage(dragRef.current.startPage + 1);
        } else if (deltaX < 0 && dragRef.current.startPage > 0) {
          setCurrentPage(dragRef.current.startPage - 1);
        }
      }
      // Otherwise snap back to current page (dragOffset → 0 with transition)

      dragRef.current.active = false;
      setDragOffset(0);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isOpen, totalPages]);

  const handleIconClick = useCallback(
    (iconConfig: DesktopIconConfig) => {
      // Don't open app if user was dragging
      const drag = dragRef.current;
      if (Math.abs(drag.startX - drag.currentX) > 10) return;
      onIconClick(iconConfig);
      onClose();
    },
    [onIconClick, onClose]
  );

  const handleSearchClick = useCallback(() => {
    onClose();
    // Small delay so Takeoff closes before Spotlight opens
    setTimeout(() => onOpenSpotlight(), 100);
  }, [onClose, onOpenSpotlight]);

  const isAppOpen = useCallback(
    (windowId: string) => {
      const instance = windows.get(windowId);
      if (!instance) return false;
      return isWindowOpen(instance.state) || isWindowMinimized(instance.state);
    },
    [windows]
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, iconId: string) => {
      e.preventDefault();
      e.stopPropagation();
      setContextMenu({ iconId, x: e.clientX, y: e.clientY });
    },
    []
  );

  // Clamp context menu position to viewport
  const getContextMenuStyle = useCallback((): React.CSSProperties => {
    if (!contextMenu) return {};
    let { x, y } = contextMenu;
    const menuWidth = 200;
    const menuHeight = 120;
    if (x + menuWidth > window.innerWidth)
      x = Math.max(0, window.innerWidth - menuWidth - 4);
    if (y + menuHeight > window.innerHeight) y = Math.max(0, y - menuHeight);
    return {
      position: "fixed",
      left: x,
      top: y,
      zIndex: 10000,
      minWidth: 180,
    };
  }, [contextMenu]);

  if (!isOpen) return null;

  // Derive context menu options for the right-clicked icon
  const inDock = contextMenu
    ? (isIconInDock?.(contextMenu.iconId) ?? false)
    : false;
  const onDesktop = contextMenu
    ? (isIconOnDesktop?.(contextMenu.iconId) ?? false)
    : false;
  const isOnDockOrDesktop = inDock || onDesktop;

  // "Move to Dock" — show when icon is NOT already in the dock
  const showMoveToDock = contextMenu && !inDock;
  // "Move to Desktop" — show when icon is NOT already on the desktop
  const showMoveToDesktop = contextMenu && !onDesktop;
  // "Hide" — only for icons that are on dock or desktop (hidden icons can't be hidden again)
  const showHide = contextMenu && isOnDockOrDesktop && onHideIcon;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9000]"
        onClick={safeClose}
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.65)",
          backdropFilter: "blur(30px)",
          WebkitBackdropFilter: "blur(30px)",
          animation: "takeoff-fade-in 0.25s ease",
        }}
      />

      {/* Content */}
      <div
        className="fixed inset-0 z-[9001] flex flex-col items-center"
        onClick={(e) => {
          if (e.target === e.currentTarget) safeClose();
        }}
        style={{ animation: "takeoff-scale-in 0.25s ease" }}
      >
        {/* Search bar + filter */}
        <div
          style={{
            marginTop: 60,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={handleSearchClick}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 20px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.2)",
                backgroundColor: "rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.5)",
                fontSize: 14,
                cursor: "pointer",
                minWidth: 240,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              Search
            </button>
            {categories.length > 0 && (
              <button
                onClick={() => {
                  setFilterOpen((open) => {
                    if (open) setSelectedCategory(null);
                    return !open;
                  });
                }}
                aria-pressed={filterOpen}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: 999,
                  border: `1px solid ${filterOpen ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.2)"}`,
                  backgroundColor: filterOpen
                    ? "rgba(255,255,255,0.25)"
                    : "rgba(255,255,255,0.1)",
                  color: filterOpen
                    ? "rgba(255,255,255,0.95)"
                    : "rgba(255,255,255,0.7)",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                Filter
              </button>
            )}
          </div>

          {filterOpen && categories.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                gap: 8,
                maxWidth: "90vw",
                overflowX: "auto",
                paddingBottom: 4,
                animation: "takeoff-fade-in 0.2s ease",
              }}
            >
              {categories.map((cat) => {
                const active = selectedCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(active ? null : cat)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 999,
                      border: `1px solid ${active ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.2)"}`,
                      backgroundColor: active
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(255,255,255,0.08)",
                      color: active
                        ? "rgba(255,255,255,1)"
                        : "rgba(255,255,255,0.75)",
                      fontSize: 12,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Pages */}
        <div
          ref={pagesContainerRef}
          className="relative w-full flex-1 overflow-hidden"
          style={{ marginTop: 30, cursor: totalPages > 1 ? "grab" : undefined }}
          onClick={(e) => {
            if (e.target === e.currentTarget) safeClose();
          }}
          onTouchStart={totalPages > 1 ? handleTouchStart : undefined}
          onTouchMove={totalPages > 1 ? handleTouchMove : undefined}
          onTouchEnd={totalPages > 1 ? handleTouchEnd : undefined}
          onMouseDown={totalPages > 1 ? handleMouseDown : undefined}
        >
          <div
            className="absolute inset-0 flex"
            style={{
              transform: `translateX(calc(-${currentPage * 100}% + ${dragOffset}%))`,
              transition:
                dragOffset !== 0 ? "none" : "transform 0.35s ease-out",
              userSelect: dragOffset !== 0 ? "none" : undefined,
            }}
          >
            {pages.map((pageIcons, pageIndex) => (
              <div
                key={pageIndex}
                className="h-full min-w-full"
                onClick={(e) => {
                  if (e.target === e.currentTarget) safeClose();
                }}
              >
                <div
                  onClick={(e) => {
                    if (e.target === e.currentTarget) safeClose();
                  }}
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
                    gridTemplateRows: `repeat(${GRID_ROWS}, 1fr)`,
                    gap: `${GRID_GAP_ROW}px ${GRID_GAP_COL}px`,
                    width: "100%",
                    height: "100%",
                    padding: `${GRID_PADDING_Y}px ${GRID_PADDING_X_RATIO * 100}%`,
                    boxSizing: "border-box",
                    justifyItems: "center",
                    alignItems: "center",
                  }}
                >
                  {pageIcons.map((iconConfig) => {
                    const appOpen = isAppOpen(iconConfig.windowId);

                    return (
                      <button
                        key={iconConfig.id}
                        onClick={() => handleIconClick(iconConfig)}
                        onContextMenu={(e) =>
                          handleContextMenu(e, iconConfig.id)
                        }
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: metrics.labelGap,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          position: "relative",
                          justifyContent: "center",
                        }}
                      >
                        {/* Actual app icon — same as dock */}
                        <div
                          style={{
                            transition: "transform 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.08)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        >
                          {typeof iconConfig.icon === "string" ? (
                            <div
                              className="flex items-center justify-center bg-white/10 shadow-lg backdrop-blur-sm"
                              style={{
                                width: metrics.iconSize,
                                height: metrics.iconSize,
                                borderRadius: metrics.iconRadius,
                                fontSize: metrics.iconFontSize,
                                lineHeight: 1,
                              }}
                            >
                              {iconConfig.icon}
                            </div>
                          ) : (
                            <div
                              style={{
                                width: metrics.iconSize,
                                height: metrics.iconSize,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <div
                                style={{
                                  transform: `scale(${metrics.iconSize / NATIVE_ICON_PX})`,
                                  transformOrigin: "center",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {iconConfig.icon}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Running indicator */}
                        {appOpen && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: metrics.labelFontSize + metrics.labelGap,
                              left: "50%",
                              transform: "translateX(-50%)",
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: "#fff",
                              boxShadow: "0 0 4px rgba(255,255,255,0.6)",
                            }}
                          />
                        )}

                        {/* Label */}
                        <span
                          style={{
                            fontSize: metrics.labelFontSize,
                            color: "rgba(255,255,255,0.85)",
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textAlign: "center",
                            paddingInline: 4,
                          }}
                        >
                          {iconConfig.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Page dots */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
              paddingBottom: 40,
            }}
          >
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index)}
                style={{
                  width: index === currentPage ? 10 : 8,
                  height: index === currentPage ? 10 : 8,
                  borderRadius: "50%",
                  backgroundColor:
                    index === currentPage
                      ? "rgba(255,255,255,0.9)"
                      : "rgba(255,255,255,0.35)",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  transition: "all 0.2s",
                }}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (showMoveToDock || showMoveToDesktop || showHide) && (
        <div
          ref={contextMenuRef}
          style={getContextMenuStyle()}
          className="overflow-hidden rounded-lg border shadow-2xl"
          role="menu"
        >
          <div
            className="py-1"
            style={{
              backgroundColor: "rgba(40, 40, 45, 0.95)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              borderColor: "rgba(255, 255, 255, 0.15)",
            }}
          >
            {showMoveToDock && (
              <button
                className={`w-full px-4 py-2 text-left text-sm transition-colors ${isDockFull ? "cursor-default" : "hover:bg-blue-500 hover:text-white"}`}
                style={{
                  color: isDockFull ? "rgba(228, 228, 231, 0.35)" : "#e4e4e7",
                }}
                onClick={
                  isDockFull
                    ? undefined
                    : () => {
                        onMoveToDock?.(contextMenu.iconId);
                        setContextMenu(null);
                      }
                }
                role="menuitem"
                aria-disabled={isDockFull}
              >
                {isDockFull ? "Move to Dock (Full)" : "Move to Dock"}
              </button>
            )}
            {showMoveToDesktop && (
              <button
                className="w-full px-4 py-2 text-left text-sm transition-colors hover:bg-blue-500 hover:text-white"
                style={{ color: "#e4e4e7" }}
                onClick={() => {
                  onMoveToDesktop?.(contextMenu.iconId);
                  setContextMenu(null);
                }}
                role="menuitem"
              >
                Move to Desktop
              </button>
            )}
            {showHide && (
              <>
                {(showMoveToDock || showMoveToDesktop) && (
                  <div
                    style={{
                      height: 1,
                      margin: "2px 8px",
                      backgroundColor: "rgba(255,255,255,0.1)",
                    }}
                  />
                )}
                <button
                  className="w-full px-4 py-2 text-left text-sm transition-colors hover:bg-blue-500 hover:text-white"
                  style={{ color: "#e4e4e7" }}
                  onClick={() => {
                    onHideIcon!(contextMenu.iconId);
                    setContextMenu(null);
                  }}
                  role="menuitem"
                >
                  Hide
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes takeoff-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes takeoff-scale-in {
          from { opacity: 0; transform: scale(1.1); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </>
  );
}
